import { EmblaCarouselBarButton, useBarButton } from '@components/display/emblaCarousel';
import { VictronImage } from '@components/display/images';
import { LinearProgress, linearProgressClasses, Skeleton } from '@mui/material';
import clsx from 'clsx';
import Autoplay, { AutoplayOptionsType, AutoplayType } from 'embla-carousel-autoplay';
import useEmblaCarousel, { EmblaViewportRefType } from 'embla-carousel-react';
import { ComponentPropsWithRef, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const AutoplayDelay = 10000;

type PropType = {
    slides: { src: string; loading?: boolean }[];
    useVanillaComponents?: boolean;
};

type AutoCarouselController = {
    emblaRef: ReturnType<typeof useEmblaCarousel>[0];
    autoplay?: AutoplayType;
    selectedIndex: number;
    scrollSnaps: number[];
    onBarButtonClick: (index: number) => void;
};

type ProgressiveScrollSnapProps = ComponentPropsWithRef<'button'> & {
    variant?: 'pill' | 'dot';
    isActive: boolean;
    isSingleSlide?: boolean;
    autoplay?: AutoplayType;
    useVanillaComponents?: boolean;
    delay?: number;
};

type CarouselSlidesProps = {
    slides: { src: string; loading?: boolean; draft?: boolean }[];
    gradient?: boolean;
    useVanillaComponents?: boolean;
    className?: string;
    renderSlideOverlay?: (index: number) => React.ReactNode;
};

type ProgressiveScrollSnapBarProps = {
    selectedIndex: number;
    scrollSnaps: number[];
    onBarButtonClick: (index: number) => void;
    className?: string;
    buttonProps?: Omit<ProgressiveScrollSnapProps, 'isActive'>;
    carousel?: {
        ref: EmblaViewportRefType;
    };
};

const ProgressiveScrollSnap = ({
    variant = 'pill',
    isActive,
    isSingleSlide = false,
    autoplay,
    useVanillaComponents,
    delay = AutoplayDelay,
    ...props
}: ProgressiveScrollSnapProps) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!isActive) {
            setProgress(0);
            return;
        }

        if (isSingleSlide) {
            setProgress(100);
            return;
        }

        const timer = setInterval(() => {
            if (!autoplay?.isPlaying()) {
                return;
            }

            setProgress((oldProgress) => {
                if (oldProgress === 100) {
                    return 100;
                }

                const elapsed = delay - Number(autoplay.timeUntilNext());
                return Math.min(100, Math.max(0, (elapsed / delay) * 100));
            });
        }, 0);

        return () => {
            clearInterval(timer);
        };
    }, [autoplay, isActive, isSingleSlide]);

    return (
        <EmblaCarouselBarButton
            {...props}
            className={clsx(
                'bg-[#FFFFFF90] rounded-full cursor-pointer relative overflow-hidden transition-all',
                props.className,
                {
                    'flex-1': variant === 'pill',
                    'flex-[0_0_8px] h-[8px]': variant === 'dot',
                    '!flex-[0_0_40px]': variant === 'dot' && isActive,
                }
            )}
        >
            {isActive ? (
                useVanillaComponents ? (
                    <div className="w-full h-2 bg-[#a7caed] rounded-full overflow-hidden">
                        <div
                            className="h-2 bg-primary-600 transition-all duration-75"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                ) : (
                    <LinearProgress
                        value={progress}
                        variant="determinate"
                        sx={{
                            height: '100%',
                            ...(variant === 'dot'
                                ? {
                                      backgroundColor: 'rgba(255, 255, 255, 0.56)', // Transparent white track
                                      [`& .${linearProgressClasses.bar}`]: {
                                          backgroundColor: '#ffffff', // Solid white moving bar
                                      },
                                  }
                                : {}),
                        }}
                    />
                )
            ) : null}
        </EmblaCarouselBarButton>
    );
};

const CarouselSlides = ({
    slides,
    gradient = true,
    useVanillaComponents,
    className,
    renderSlideOverlay,
}: CarouselSlidesProps) => (
    <div className="flex touch-pan-y touch-pinch-zoom h-full">
        {slides.map((slide, index) => (
            <div
                className="flex-[0_0_100%] min-w-none mr-[16px] relative"
                key={index}
                style={{ transform: 'translate3d(0,0,0)' }}
            >
                {slide.loading ? (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        width="100%"
                        height={260}
                        sx={{
                            bgcolor: '#fff',
                        }}
                    />
                ) : (
                    <>
                        {useVanillaComponents ? (
                            <img src={slide.src} className="w-full h-full object-cover sm:rounded-lg" />
                        ) : (
                            <VictronImage
                                src={slide.src}
                                className={clsx('w-full h-full object-cover sm:rounded-lg', className)}
                            />
                        )}
                        {gradient && (
                            <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black to-transparent pointer-events-none sm:rounded-b-lg" />
                        )}
                        {slide.draft && (
                            <div className="absolute top-4 left-4 rounded-[24px] bg-signal-warning py-[4px] px-[12px]">
                                <span className="text-white text-base font-medium font-inter leading-[120%]">
                                    <FormattedMessage id="common.status.draft" defaultMessage="Draft" />
                                </span>
                            </div>
                        )}
                        {renderSlideOverlay?.(index)}
                    </>
                )}
            </div>
        ))}
    </div>
);

const ProgressiveScrollSnapBar = ({
    selectedIndex,
    scrollSnaps,
    className,
    buttonProps,
    onBarButtonClick,
    carousel,
}: ProgressiveScrollSnapBarProps) => {
    return !carousel ? (
        <div className={className}>
            {scrollSnaps.map((scrollSnap, index) => (
                <ProgressiveScrollSnap
                    key={`${scrollSnap}-${index}`}
                    isActive={selectedIndex === index}
                    isSingleSlide={scrollSnaps.length === 1}
                    onClick={() => {
                        buttonProps?.autoplay?.reset();
                        onBarButtonClick?.(index);
                    }}
                    {...buttonProps}
                />
            ))}
        </div>
    ) : (
        <div className={clsx('overflow-hidden', className)} ref={carousel.ref}>
            <div className="flex flex-row">
                {scrollSnaps.map((scrollSnap, index) => (
                    <ProgressiveScrollSnap
                        key={`${scrollSnap}-${index}`}
                        isActive={selectedIndex === index}
                        onClick={() => {
                            buttonProps?.autoplay?.reset();
                            onBarButtonClick?.(index);
                        }}
                        isSingleSlide={scrollSnaps.length === 1}
                        {...buttonProps}
                        className={clsx('min-w-none mr-[8px]', buttonProps?.className)}
                    />
                ))}
            </div>
        </div>
    );
};

const useAutoCarouselController = ({
    loop = true,
    delay = AutoplayDelay,
    autoplayProps,
}: {
    loop?: boolean;
    delay?: number;
    autoplayProps?: AutoplayOptionsType;
}): AutoCarouselController => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop, watchDrag: (api) => api.slideNodes().length > 1 }, [
        Autoplay({ delay, stopOnInteraction: false, ...autoplayProps }),
    ]);

    const { selectedIndex, scrollSnaps, onBarButtonClick } = useBarButton(emblaApi);

    return {
        emblaRef,
        autoplay: emblaApi?.plugins().autoplay,
        selectedIndex,
        scrollSnaps,
        onBarButtonClick,
    };
};

const AutoCarousel = ({ slides, useVanillaComponents }: PropType) => {
    const { emblaRef, autoplay, selectedIndex, scrollSnaps, onBarButtonClick } = useAutoCarouselController({});

    return (
        <section className="relative h-full">
            <div className="overflow-hidden h-full" ref={emblaRef}>
                <CarouselSlides slides={slides} />
            </div>
            <ProgressiveScrollSnapBar
                selectedIndex={selectedIndex}
                scrollSnaps={scrollSnaps}
                onBarButtonClick={onBarButtonClick}
                className="absolute bottom-5 left-1/2 -translate-x-1/2 w-1/2 flex gap-2 h-[4px]"
                buttonProps={{
                    variant: 'pill',
                    autoplay,
                    useVanillaComponents,
                }}
            />
        </section>
    );
};

export { AutoCarousel, CarouselSlides, ProgressiveScrollSnapBar, useAutoCarouselController };
